import { useState } from "react";
import { Highlight, themes } from "prism-react-renderer";
import { Copy, Check, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export function CodeBlock({ code, language = "bash", filename = "script.sh" }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 1500);
  };

  const download = () => {
    const blob = new Blob([code], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${filename}`);
  };

  return (
    <div className="border border-[#27272A] bg-[#050505]" data-testid="code-block">
      <div className="flex items-center justify-between border-b border-[#27272A] px-4 py-2.5 bg-[#0A0A0B]">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-[#FF5F56]" />
          <span className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
          <span className="w-3 h-3 rounded-full bg-[#27C93F]" />
          <span className="ml-3 font-mono text-xs text-[#71717A]">{filename}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="copy-code-btn"
            onClick={copy}
            className="flex items-center gap-1.5 border border-[#27272A] px-3 py-1.5 text-xs text-[#A1A1AA] hover:text-[#00F0FF] hover:border-[#00F0FF] transition-colors duration-200"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? "Copied" : "Copy"}
          </button>
          <button
            data-testid="download-code-btn"
            onClick={download}
            className="flex items-center gap-1.5 border border-[#27272A] px-3 py-1.5 text-xs text-[#A1A1AA] hover:text-[#00FF41] hover:border-[#00FF41] transition-colors duration-200"
          >
            <DownloadSimple size={14} /> Download
          </button>
        </div>
      </div>
      <Highlight theme={themes.vsDark} code={code || ""} language={language}>
        {({ style, tokens, getLineProps, getTokenProps }) => (
          <pre
            className="overflow-auto p-4 text-xs md:text-sm font-mono leading-relaxed max-h-[560px]"
            style={{ ...style, background: "#050505" }}
          >
            {tokens.map((line, i) => (
              <div key={i} {...getLineProps({ line })}>
                <span className="inline-block w-8 select-none text-[#3f3f46] text-right pr-3">
                  {i + 1}
                </span>
                {line.map((token, key) => (
                  <span key={key} {...getTokenProps({ token })} />
                ))}
              </div>
            ))}
          </pre>
        )}
      </Highlight>
    </div>
  );
}
